<template>
    <div class="no-header">
        <div ref="smsWrapper" class="container">
            <div class="buttons hideForPrint">
                <span v-if="showBack" @click="goBack()" class="button is-info">
                    <span><font-awesome-icon icon="chevron-left" /></span> &nbsp; Back
                </span>
            </div>

            <div v-if="loadingData">
                <span><font-awesome-icon icon="cog" class="fa-spin" />&nbsp;Loading custom log data...</span>
                <div class="spacer s40"></div>
            </div>

            <div v-else>
                <div v-if="dataError">
                    <h3 class="title">{{ errorMessage }}</h3>

                    <p>
                        Refresh the page and if the issue remains, please either give us a call on
                        <a href="tel:+61-447-139-382">+61 447 139 382</a>, or email
                        <a href="mailto:support@offshoresms.com.au?subject=Question from Offshore SMS">support@offshoresms.com.au</a>.
                    </p>
                </div>

                <div v-else>
                    <h3
                        style="
                            font-family: 'helvetica', sans-serif;
                            color: #444;
                            font-size: 1.2rem;
                            font-weight: bold;
                            text-transform: uppercase;
                            margin-top: 1.5rem;
                            display: block;
                        ">
                        {{ logData.realVesselName ?? vesselName }}: {{ logData.customChecklistName }}
                    </h3>
                    <p style="font-family: 'helvetica', sans-serif; color: #444; font-size: 1rem; margin-top: 6px; margin-bottom: 5px">
                        Logged at:
                        <span v-if="logData.logTimestamp">{{ showDate(logData.logTimestamp) }}</span>
                    </p>
                    <p style="font-family: 'helvetica', sans-serif; color: #444; font-size: 1rem; margin-top: 6px; margin-bottom: 5px">
                        By: {{ logData.postedBy }}
                    </p>
                    <p
                        v-if="logData.tripStarted"
                        style="font-family: 'helvetica', sans-serif; color: mediumseagreen; font-size: 1rem; margin-top: 6px; margin-bottom: 25px">
                        <font-awesome-icon icon="star" /> &nbsp; New trip activated from this log
                    </p>

                    <div class="field-type checkbox-wrapper clean mt-5">
                        <input class="is-checkradio" type="checkbox" id="showReadOnly" v-model="showReadOnly" />
                        <label for="showReadOnly">
                            Display read only fields &nbsp;
                            <span v-if="showReadOnly" class="green"><font-awesome-icon icon="eye" /></span>
                            <span v-else><font-awesome-icon icon="eye-slash" /></span>
                        </label>
                        <input class="is-checkradio" type="checkbox" id="showConditionals" v-model="showConditionals" />
                        <label for="showConditionals">
                            Show conditional fields &nbsp;
                            <span v-if="showConditionals" class="green"><font-awesome-icon icon="eye" /></span>
                            <span v-else><font-awesome-icon icon="eye-slash" /></span>
                        </label>
                    </div>

                    <table class="table is-striped is-fullwidth mt-4">
                        <tbody>
                            <tr v-for="(item, indx) in answerPool" v-show="canShowRow(item)" :key="`item-${indx}`">
                                <th>
                                    <div>
                                        <div class="mb-1">
                                            {{ item.label }}
                                        </div>
                                        <div class="tags">
                                            <div class="tag pink" v-if="item.isConditional && showConditionals">
                                                <font-awesome-icon icon="link" />&nbsp; Conditional
                                            </div>
                                            <div class="tag blue" v-if="item.type == 'dumbtext' || item.type == 'dumbphoto'">
                                                <font-awesome-icon icon="eye" />&nbsp; Read only
                                            </div>
                                        </div>
                                    </div>
                                    <div class="copy-link" @click="copyText(false, false, item.label)">
                                        <font-awesome-icon icon="link" />
                                    </div>
                                </th>
                                <td>
                                    <div
                                        v-if="
                                            item.type != 'doodle' &&
                                            item.type != 'dumbtext' &&
                                            item.type != 'dumbphoto' &&
                                            item.type != 'table' &&
                                            item.type != 'datetime' &&
                                            item.type != 'pindrop' &&
                                            item.type != 'crew'
                                        "
                                        class="copy-link"
                                        @click="copyText(Array.isArray(item.answer), item.type == 'datetime', item.answer)">
                                        <font-awesome-icon icon="link" />
                                    </div>
                                    <FormItemAnswer
                                        :item="item"
                                        :get-text-answer="getTextAnswer"
                                        :simple-date="simpleDate"
                                        :get-avatar="getAvatar"
                                        :is-checkbox="isCheckbox"
                                        :to-what3-words="toWhat3Words"
                                        :zoom-in="zoomIn" />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="buttons">
                        <span id="pdfButton" class="button is-primary" :class="{ 'is-loading': loadingExportToPdf }" @click="exportToPdf()"
                            >Export to PDF</span
                        >
                    </div>
                </div>
            </div>

            <div class="copy-prompt hideForPrint" :class="{ visible: showCopyPrompt }">Copied to clipboard!</div>
        </div>

        <div class="modal modal-full-screen modal-fx-fadeInScale" v-bind:class="{ 'is-active': showZoomed }">
            <div class="modal-background" @click="close"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">Need more information?</p>
                    <button class="delete" aria-label="close" @click.prevent="close"></button>
                </header>

                <section class="modal-card-body">
                    <div class="full-img">
                        <img :src="selectedImage" alt="loading" />
                    </div>
                </section>

                <footer class="modal-card-foot">
                    <button class="button cancel" @click.prevent="close">Done</button>
                </footer>
            </div>
        </div>

        <div class="spacer s50"></div>
    </div>
</template>

<script>
import { db } from '../main.js';
import { collection, query, doc, getDocs, getDoc, orderBy } from 'firebase/firestore';
import NProgress from 'nprogress';

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import offshoresmsLogo from '@/assets/img/offshoresms-new-logo.png';
import html2canvas from 'html2canvas';
import FormItemAnswer from '@/reusables/FormItemAnswer.vue';

import { OpenSansFont, OpenSansBoldFont } from '@/assets/fonts.js';

import { simpleDate } from '@/utilities/helperFunctions';

function sanitizeText(text) {
    if (!text) return '';

    return text.replace(/\u00A0/g, ' ').replace(/•\s*/g, '- ');
}

export default {
    name: 'CustomLogResponse',

    props: {
        sentVesselID: String,
        sentVesselName: String,
        showBack: Boolean
    },

    components: { FormItemAnswer },

    mounted() {
        const rvid = this.$route.query.rvid;
        this.finalVesselID = rvid ? rvid : this.$route.query.vid ? this.$route.query.vid : '';
        this.finalLogID = this.$route.query.lid ? this.$route.query.lid : '';
        this.isFleetLog = this.$route.query.f ? this.$route.query.f == '1' : false;

        if (this.finalVesselID != '' && this.finalLogID != '') {
            this.loadLogData();
        } else {
            setTimeout(() => {
                this.dataError = true;
                this.errorMessage = 'No log data found.';
                this.loadingData = false;
            }, 1000);
        }
    },

    data() {
        return {
            finalVesselID: '',
            finalLogID: '',
            loadingData: true,
            vesselData: {},
            fleetData: {},
            vesselOwnerID: '',
            logData: {},
            vesselName: '',
            isFleetLog: false,

            showCopyPrompt: false,

            dataError: false,
            errorMessage: '',
            showZoomed: false,
            selectedImage: '',

            yOffset: 20,
            loadingExportToPdf: false,

            showReadOnly: false,
            showConditionals: true,

            customFields: [],
            answerPool: [],
            answersCollection: {},
            responsesMap: {},

            fleetLogo: '',
            hideFleetLogoAndName: false
        };
    },

    methods: {
        toWhat3Words(words) {
            let link = 'https://what3words.com/' + words;
            window.open(link, '_blank');
        },

        zoomIn(image) {
            this.selectedImage = image;
            this.showZoomed = true;
        },

        getAvatar(crewName) {
            let parts = crewName.length > 0 ? crewName.split(' ') : [crewName];
            let initials = '';
            for (let i = 0; i < parts.length; i++) {
                if (parts[i].length > 0 && parts[i] !== '') {
                    initials += parts[i][0];
                }
            }
            return initials;
        },

        async getVesselOwnerIdOrFleet() {
            try {
                const docRef = doc(db, 'vessels', this.finalVesselID);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    this.vesselOwnerID = docSnap.data().vesselOwnerID || '';
                } else {
                    this.vesselOwnerID = this.finalVesselID;
                }
            } catch (error) {
                this.vesselOwnerID = this.finalVesselID;
            }
        },

        sanitizeSingleAnswer(type, answer) {
            switch (type) {
                case 'text':
                case 'customtext':
                case 'paragraph':
                case 'crew':
                case 'riskmatrix':
                case 'upload':
                case 'dropdown':
                    return Array.isArray(answer) ? answer : [answer];
                case 'doodle':
                    return answer == 'Choose an option' || answer == '' ? '' : answer;
                case 'checkboxes':
                    return answer;
                case 'pindrop': {
                    const latitude = answer?.latitude ?? answer?.[0] ?? '';
                    const longitude = answer?.longitude ?? answer?.[1] ?? '';
                    const what3words = answer?.what3words ?? answer?.[2] ?? '';

                    const url =
                        'https://maps.googleapis.com/maps/api/staticmap?size=600x600&zoom=7&center=' +
                        latitude +
                        ',' +
                        longitude +
                        '&scale=1&format=png&maptype=roadmap&key=AIzaSyAWTjw84LiSreWuruKtazxm7jbeK4g0k9U&markers=color:red%7C' +
                        latitude +
                        ',' +
                        longitude +
                        '&markers=color:red%7CMemphis,TN';

                    return { image: url, latitude, longitude, what3words };
                }
                case 'datetime':
                    return answer?.toDate ? answer.toDate() : answer;
                case 'dumbtext':
                    return answer;
                default:
                    return answer;
            }
        },

        sanitiseAnswers(answers) {
            return answers.map((answer) => {
                const sanitizedAnswer = { ...answer };
                sanitizedAnswer.localAnswer = this.sanitizeSingleAnswer(sanitizedAnswer.type, sanitizedAnswer.answer);
                this.responsesMap[sanitizedAnswer.id] = sanitizedAnswer.localAnswer;
                return sanitizedAnswer;
            });
        },

        async loadCustomFields(fieldsRef) {
            const q = query(fieldsRef, orderBy('index', 'asc'));
            const querySnapshot = await getDocs(q);
            this.customFields = querySnapshot.docs.map((doc) => {
                const data = doc.data();
                data.id = doc.id;
                return data;
            });
        },

        async loadNewSavedAnswers() {
            const collectionRef = collection(db, 'vessels', this.finalVesselID, 'customLogs', this.finalLogID, 'answers');
            const querySnapshot = await getDocs(collectionRef);

            for (const doc of querySnapshot.docs) {
                this.answersCollection[doc.id] = doc.data();
            }

            for (const field of this.customFields) {
                const savedAnswer = this.answersCollection[field.id];
                const sanitizedAnswer = savedAnswer
                    ? this.sanitizeSingleAnswer(field.type, savedAnswer.answer)
                    : this.sanitizeSingleAnswer(field.type, ['']);

                this.responsesMap[field.id] = sanitizedAnswer;
                this.answerPool.push({
                    ...field,
                    localAnswer: sanitizedAnswer,
                    answer: sanitizedAnswer
                });
            }
        },

        async loadLogData() {
            if (!this.finalLogID || this.finalLogID == '') {
                NProgress.done();
                return;
            }

            NProgress.start();

            try {
                // Fetch vessel owner and log data in parallel
                const logRef = doc(db, 'vessels', this.finalVesselID, 'customLogs', this.finalLogID);
                const [, logSnap] = await Promise.all([this.getVesselOwnerIdOrFleet(), getDoc(logRef)]);

                if (!logSnap.exists()) {
                    this.dataError = true;
                    this.errorMessage = 'There was an error loading this custom log.';
                    this.loadingData = false;
                    NProgress.done();
                    return;
                }

                const data = logSnap.data();
                const answers = data.customFieldAnswers ?? [];
                const customChecklistID = data.customChecklistID ?? '';

                if (customChecklistID) {
                    let fieldsRef;
                    if (this.isFleetLog) {
                        fieldsRef = collection(db, 'companies', this.vesselOwnerID, 'customChecklists', customChecklistID, 'customFields');
                    } else {
                        fieldsRef = collection(db, 'vessels', this.finalVesselID, 'customChecklists', customChecklistID, 'customFields');
                    }
                    await this.loadCustomFields(fieldsRef);
                }

                if (data.newFormat) {
                    await this.loadNewSavedAnswers();
                } else {
                    this.answerPool = this.sanitiseAnswers(answers);
                }

                data.id = logSnap.id;
                data.realVesselID = data.realVesselID || data.vesselID || '';
                data.realVesselName = data.realVesselName || data.vesselName || '';
                this.logData = data;
                this.loadingData = false;
            } catch (error) {
                console.log('Error loading custom log data:', error);
                this.dataError = true;
                this.errorMessage = 'There was an error loading this custom log.';
                this.loadingData = false;
            }
            NProgress.done();
        },

        copyText(isArray, isDate, data) {
            const dateString = isDate ? data.toDate().toLocaleString('en-US', { timeZone: 'Australia/Sydney' }) : '';
            const copyData = isArray ? data.join('\r\n') : isDate ? dateString : data;
            this.$copyText(copyData).then(
                () => this.showCopied(),
                (e) => {
                    alert('This data cannot be copied');
                    console.log(e);
                }
            );
        },

        showCopied() {
            this.showCopyPrompt = true;
            setTimeout(() => {
                this.showCopyPrompt = false;
            }, 1000);
        },

        close() {
            this.showZoomed = false;
            this.selectedImage = '';
        },

        showDate(d) {
            const dateOptions = {
                timeZone: 'Australia/Sydney',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            };
            const t = new Date(d.toDate());
            return t.toLocaleDateString('en-US', dateOptions);
        },

        goBack: function () {
            this.$router.go(-1);
        },

        getTextAnswer(answer) {
            let t = Array.isArray(answer) ? answer : [answer];
            return t.filter((n) => n != 'Choose an option' && n != null && n != undefined);
        },

        isCheckbox(item, row, column) {
            const cellData = item.tableData[(row - 1) * item.tableCols + (column - 1)];
            return cellData?.type === 'check';
        },

        canShowRow(field) {
            let isConditional = field.isConditional ?? false;
            if ((field.type == 'dumbtext' || field.type == 'dumbphoto') && !this.showReadOnly) {
                return false;
            }
            if (isConditional && this.showConditionals) {
                const conditions = Array.isArray(field?.fieldConditions) ? field.fieldConditions : [];
                if (conditions.length === 0) return true;

                const getVals = (id) => this.responsesMap?.[id];

                // OR logic (ANY): if any condition passes OR if the source field is missing, show it
                if (field?.conditionType === 'ANY') {
                    for (const condition of conditions) {
                        const vals = getVals(condition.sourceFieldID);
                        if (vals == null) return true;
                        if (Array.isArray(vals)) {
                            if (vals.includes(condition.sourceOptionValue)) return true;
                        } else {
                            if (vals === condition.sourceOptionValue) return true;
                        }
                    }
                    return false;
                }

                // AND logic (ALL / default): every condition must pass (missing source => fail)
                return conditions.every((condition) => {
                    const vals = getVals(condition.sourceFieldID);
                    if (vals == null) return false;
                    return Array.isArray(vals) ? vals.includes(condition.sourceOptionValue) : vals === condition.sourceOptionValue;
                });
            } else {
                return true;
            }
        },

        simpleDate,

        simpleDate2(sentDate) {
            const dateOptions = {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            };
            return sentDate.toLocaleDateString('en-US', dateOptions);
        },

        async createTableImage(item) {
            const tableEl = document.createElement('table');
            tableEl.style.borderCollapse = 'collapse';
            tableEl.style.display = 'table';
            tableEl.style.fontFamily = 'Arial';
            tableEl.style.fontSize = '12px';
            tableEl.style.border = '2px solid #888';
            tableEl.style.width = '510px';
            tableEl.style.position = 'absolute';
            tableEl.style.left = '-9999px';

            const tbody = document.createElement('tbody');
            for (let r = 0; r < item.tableRows; r++) {
                const tr = document.createElement('tr');
                for (let c = 0; c < item.tableCols; c++) {
                    const isHeader =
                        (item.tableHeaders === 'both' && (r === 0 || c === 0)) ||
                        (item.tableHeaders === 'top' && r === 0) ||
                        (item.tableHeaders === 'left' && c === 0);
                    const td = document.createElement(isHeader ? 'th' : 'td');

                    td.style.border = '0.5px solid #777';
                    td.style.padding = '3px';
                    td.style.verticalAlign = 'top';
                    td.style.minWidth = '50px';
                    td.style.whiteSpace = 'normal';
                    td.style.overflowWrap = 'break-word';
                    td.style.wordBreak = 'break-word';

                    if (isHeader) {
                        td.style.fontWeight = 'bold';
                        td.style.backgroundColor = '#f5f5f5';
                    }

                    const cellData = item.tableData[r * item.tableCols + c];
                    const isCheckbox = cellData.type === 'check';

                    if (isCheckbox) {
                        td.innerText = item.answer?.[r * item.tableCols + c] ? '✓' : '';
                    } else {
                        td.innerText = item.answer?.[r * item.tableCols + c] || '';
                    }

                    tr.appendChild(td);
                }
                tbody.appendChild(tr);
            }
            tableEl.appendChild(tbody);
            document.body.appendChild(tableEl);

            let processedImages = [];

            try {
                const canvas = await html2canvas(tableEl, {
                    backgroundColor: '#ffffff',
                    scale: 1,
                    useCORS: true
                });

                const base64 = canvas.toDataURL('image/jpeg', 0.95);

                const pxToMm = (px) => (px * 25.4) / 96;
                const displayWidth = pxToMm(canvas.width);
                const displayHeight = pxToMm(canvas.height);

                processedImages = [{ base64, displayWidth, displayHeight }];
            } catch (err) {
                console.error('Error generating table image:', err);
            } finally {
                document.body.removeChild(tableEl);
            }

            return processedImages;
        },

        async exportToPdf() {
            this.loadingExportToPdf = true;

            const doc = new jsPDF('p', 'mm', 'a4');
            this.loadFonts(doc);
            doc.setLineHeightFactor(1.4);
            this.yOffset = 20;

            const title = 'Completed Log - ' + this.logData.customChecklistName;
            await this.drawLogoAndTitles(doc, title);

            // First table
            autoTable(doc, {
                head: [[{ content: 'Log Information', colSpan: 2 }]],
                body: [
                    ['Asset Name', this.logData.realVesselName],
                    ['Form Name', this.logData.customChecklistName],
                    ['Log Date', this.simpleDate(this.logData.logTimestamp, true)],
                    ['Logged By', this.logData.postedBy],
                    ...(this.logData.keyFieldResult ? [['Key Field', this.logData.keyFieldResult]] : [])
                ],
                theme: 'grid',
                startY: this.yOffset,
                margin: { top: 20, right: 10, bottom: 20, left: 10 },
                styles: {
                    font: 'OpenSans',
                    fontStyle: 'normal',
                    fontSize: 11,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fontStyle: 'bold',
                    halign: 'center'
                },
                columnStyles: {
                    0: {
                        fontStyle: 'bold',
                        cellWidth: 95
                    }
                }
            });

            // Set yOffset for the next table
            this.yOffset = doc.lastAutoTable.finalY + 10;
            const maxPageHeight = doc.internal.pageSize.height - 20;
            if (this.yOffset > maxPageHeight) {
                doc.addPage();
                this.yOffset = 20;
            }

            const printedAnswers = [...this.logData.customFieldAnswers];

            // Preprocess images, doodles, uploads, and tables in parallel
            let hasImagesOrTables = false;
            await Promise.all(
                printedAnswers.map(async (item) => {
                    if (
                        (item.type === 'doodle' && item.answer !== '' && item.answer !== 'Choose an option') ||
                        item.type === 'upload' ||
                        item.type === 'dumbphoto'
                    ) {
                        hasImagesOrTables = true;
                        const imageUrls = item.type === 'dumbphoto' ? [item.prompt] : Array.isArray(item.answer) ? item.answer : [item.answer];
                        item.processedImages = await this.processImages(imageUrls, 40, 50, 0.2);
                    } else if (item.type === 'table' && item.tableRows && item.tableCols) {
                        hasImagesOrTables = true;
                        item.processedImages = await this.createTableImage(item);
                    }
                })
            );

            // Second table
            autoTable(doc, {
                head: [[{ content: 'Log Responses', colSpan: 2 }]],
                body: this.logData.customFieldAnswers.map((item) => {
                    let answer = '';
                    if (item.type === 'checkboxes') {
                        answer = (item.answer || []).map((point) => `• ${point}`).join('\n');
                    } else if (item.type === 'text' || item.type === 'paragraph' || item.type === 'dropdown') {
                        answer = this.getTextAnswer(item.answer);
                    } else if (item.type === 'pindrop') {
                        if (!item.answer || !item.answer.length) {
                            answer = 'Not completed';
                        } else {
                            answer = `LATITUDE: ${item.answer[0]}\nLONGITUDE: ${item.answer[1]}\nWhat3Words: ${item.answer[2]}`;
                        }
                    } else if (item.type === 'datetime') {
                        answer = item.answer == 'Choose an option' ? 'Not completed' : this.simpleDate(item.answer, true);
                    } else if (item.type === 'crew') {
                        answer = item.answer.map((crew) => `• ${crew.name}`).join('\n');
                    } else if (item.type === 'riskmatrix') {
                        answer = this.getTextAnswer(item.answer)[0] ? this.getTextAnswer(item.answer)[0].toUpperCase() : '';
                    } else if (item.type === 'doodle' && (item.answer === '' || item.answer === 'Choose an option')) {
                        answer = 'Not completed';
                    } else if (item.type === 'doodle' || item.type === 'upload' || item.type === 'dumbphoto' || item.type === 'table') {
                        answer = '';
                    } else if (item.type === 'dumbtext') {
                        answer = this.getTextAnswer(sanitizeText(item.prompt));
                    } else if (item.type === 'number') {
                        answer = item.answer[0] ?? '';
                    } else {
                        answer = '';
                    }

                    return [item.label, answer];
                }),
                theme: 'grid',
                startY: this.yOffset,
                margin: { top: 20, right: 10, bottom: 20, left: 10 },
                styles: {
                    font: 'OpenSans',
                    fontStyle: 'normal',
                    fontSize: 11,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fontStyle: 'bold',
                    halign: 'center'
                },
                columnStyles: {
                    0: {
                        fontStyle: 'bold',
                        cellWidth: 95
                    }
                },
                rowPageBreak: 'avoid',
                didParseCell: (data) => {
                    if (!hasImagesOrTables || data.row.section === 'head' || data.column.index === 0) return;

                    const item = printedAnswers[data.row.index];
                    if (item == undefined || item == null) return;
                    if (
                        (item.type === 'doodle' || item.type === 'upload' || item.type === 'dumbphoto' || item.type === 'table') &&
                        item.processedImages &&
                        item.processedImages.length
                    ) {
                        const imageHeight = Math.max(...item.processedImages.map((img) => img.displayHeight));
                        data.cell.styles.minCellHeight = imageHeight + 4;
                    }
                },
                didDrawCell: (data) => {
                    if (!hasImagesOrTables || data.row.section === 'head' || data.column.index === 0) return;

                    const item = printedAnswers[data.row.index];
                    if (item == undefined || item == null) return;
                    if (
                        (item.type === 'doodle' || item.type === 'upload' || item.type === 'dumbphoto' || item.type === 'table') &&
                        item.processedImages &&
                        item.processedImages.length
                    ) {
                        let imageX = data.cell.x + 2;
                        const imageY = data.cell.y + 2;

                        for (const { base64, displayWidth, displayHeight } of item.processedImages) {
                            doc.addImage(base64, 'JPEG', imageX, imageY, displayWidth, displayHeight);
                            imageX += displayWidth + 3;
                        }
                    }
                }
            });

            this.addFooter(doc);
            this.generateFile(doc);

            this.loadingExportToPdf = false;
        },

        loadFonts(doc) {
            doc.addFileToVFS('OpenSansFont.otf', OpenSansFont);
            doc.addFont('OpenSansFont.otf', 'OpenSans', 'normal');
            doc.addFileToVFS('OpenSansBoldFont.otf', OpenSansBoldFont);
            doc.addFont('OpenSansBoldFont.otf', 'OpenSans', 'bold');
        },

        async drawLogoAndTitles(doc, title) {
            if (!this.hideFleetLogoAndName) {
                let imageDimensions = null;
                if (this.fleetLogo) {
                    const imageType = this.fleetLogo.split('.').pop().toLowerCase() === 'png' ? 'image/png' : 'image/jpeg';
                    const imageDimensionsArray = await this.processImages([this.fleetLogo], 51, 19.5, 1, imageType);
                    imageDimensions = imageDimensionsArray[0];
                }
                if (!imageDimensions) {
                    const logo = offshoresmsLogo;
                    const imageDimensionsArray = await this.processImages([logo], 51, 19.5, 1, 'image/png');
                    imageDimensions = imageDimensionsArray[0];
                }
                const { base64, displayWidth, displayHeight } = imageDimensions;
                const pageWidth = doc.internal.pageSize.width;
                const centerX = (pageWidth - displayWidth) / 2;
                doc.addImage(base64, 'JPEG', centerX, this.yOffset, displayWidth, displayHeight);
                this.yOffset += displayHeight + 6;
            }

            this.createTitle(doc, title);
        },

        createTitle(doc, text) {
            text = sanitizeText(text);
            doc.setFont('OpenSans', 'bold');
            doc.setFontSize(16);
            const pageWidth = doc.internal.pageSize.width;
            const margin = 10;
            const maxWidth = pageWidth - margin * 2;
            const lines = doc.splitTextToSize(text, maxWidth);
            lines.forEach((line, index) => {
                const textWidth = doc.getTextWidth(line);
                doc.text(line, (pageWidth - textWidth) / 2, this.yOffset + index * 7);
            });
            this.yOffset += lines.length * 7;
        },

        async processImages(images, maxWidth, maxHeight, compressionQuality, type = 'image/jpeg') {
            if (!images.length) return [];

            const results = await Promise.all(
                images.map(async (imageUrl) => {
                    try {
                        const { base64, width, height } = await this.getImageDimensions(imageUrl, compressionQuality, type);
                        const scaleFactor = Math.min(maxWidth / width, maxHeight / height, 1);
                        return { base64, displayWidth: width * scaleFactor, displayHeight: height * scaleFactor };
                    } catch (error) {
                        console.error('Error loading image:', imageUrl, error);
                        return null;
                    }
                })
            );

            return results.filter(Boolean);
        },

        async getImageDimensions(url, compressionQuality, type) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.src = url;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    resolve({
                        base64: canvas.toDataURL(type, compressionQuality),
                        width: img.width,
                        height: img.height
                    });
                };
                img.onerror = reject;
            });
        },

        addFooter(doc) {
            const pageCount = doc.internal.getNumberOfPages();
            const pageHeight = doc.internal.pageSize.height;
            const currentDate = this.simpleDate2(new Date());

            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);

                const footerLineY = pageHeight - 15;
                doc.setDrawColor(136);
                doc.line(10, footerLineY, doc.internal.pageSize.width - 10, footerLineY);

                doc.setFont('OpenSans', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(136);

                doc.text('OffshoreSMS', 10, footerLineY + 5);

                const pageWidth = doc.internal.pageSize.width;
                doc.text(currentDate, pageWidth / 2, footerLineY + 5, { align: 'center' });

                doc.text(`Page ${i} of ${pageCount}`, pageWidth - 10, footerLineY + 5, { align: 'right' });
            }
        },

        generateFile(doc) {
            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');

            const filename = 'Completed Log - ' + this.logData.customChecklistName + '.pdf';
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = filename;
            link.click();
        }
    }
};
</script>
<style lang="scss" scoped>
#app .header {
    position: relative !important;
}

.modal {
    &.modal-full-screen {
        img {
            max-width: 100%;
        }
    }
}

.container {
    max-width: 1140px !important;
}

.edit-note {
    margin-top: 1rem;
    font-size: 1rem;
    color: steelblue;
    font-style: italic;
}

.copy-prompt {
    position: fixed;
    top: 80px;
    right: 20px;
    background: #a4dba4;
    color: #fff;
    padding: 1rem;
    border-radius: 6px;
    opacity: 0;
    transition: 0.2s;
    z-index: 9999;
    &.visible {
        opacity: 1;
    }
}

.crew-member {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 12px;
    position: relative;
    .avatar-wrapper {
        .avatar {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: #f0f0f0;
            color: #444;
            font-weight: bold;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 1.2rem;
            margin-right: 12px;
            background-position: center center;
            background-size: cover;
        }
        .crew-name {
            font-weight: normal;
        }
    }
}

.uploaded-image {
    img {
        max-width: 100%;
        width: auto;
    }
}

hr {
    margin-bottom: 4rem !important;

    &.narrow {
        margin-bottom: 1.5rem !important;
    }
}

h3 {
    font-size: 1.2rem !important;
    text-transform: uppercase !important;
    margin-top: 2.5rem !important;
}

table {
    margin-top: 1rem !important;
    table-layout: fixed !important;
    width: 100% !important;

    tr {
        th {
            font-weight: normal !important;
            border: 1px solid #d4d4d4;
            padding: 12px 20px;
            font-family: 'Open Sans', sans-serif;
            text-align: left !important;
            word-break: break-word;
            position: relative;
            .copy-link {
                position: absolute;
                top: 0;
                right: 0;
                z-index: 99;
                width: 40px;
                height: 40px;
                background: #f4f4f4;
                align-items: center;
                justify-content: center;
                display: none;
                cursor: pointer;
            }
            &:hover {
                .copy-link {
                    display: flex;
                }
            }
            &.grey {
                background-color: #f4f4f4;
            }
        }

        td {
            width: 50;
            border: 1px solid #d4d4d4 !important;
            padding: 8px 12px;
            font-family: 'Open Sans', sans-serif;
            text-align: left !important;
            word-break: break-word;
            position: relative;
            .link {
                margin-top: 5px;
            }
            .copy-link {
                position: absolute;
                top: 0;
                right: 0;
                z-index: 99;
                width: 40px;
                height: 40px;
                background: #f4f4f4;
                align-items: center;
                justify-content: center;
                display: none;
                cursor: pointer;
            }
            &:hover {
                .copy-link {
                    display: flex;
                }
            }
        }
    }
}

.table {
    margin-top: 1rem !important;
    table-layout: fixed !important;
    width: 100% !important;

    tbody {
        tr {
            th {
                border: 1px solid #d4d4d4;
                padding: 12px 20px;
                font-family: 'Open Sans', sans-serif;
                text-align: left !important;
                word-break: break-word;
                position: relative;
                width: 30%;
                font-weight: bold;
            }

            td {
                width: 50%;
                border: 1px solid #d4d4d4 !important;
                padding: 12px 20px;
                font-family: 'Open Sans', sans-serif;
                text-align: left !important;
                word-break: break-word;
            }
            &:last-child th {
                border-bottom-width: auto;
            }
        }
    }
}

.image-wrap2 {
    display: inline-block;
    margin-right: 1rem;
    width: 200px;
    border: 1px solid #ddd;
    cursor: pointer;

    img {
        object-fit: contain;
        margin: auto;
        padding: 0;
        height: 250px;
        display: block;
    }
}

.pagebreak {
    display: block !important;
    clear: both !important;
    page-break-after: always !important;
}

@media only screen and (max-width: 550px) {
    table,
    tbody,
    tr,
    th,
    td {
        display: block;
    }
    tr {
        margin-bottom: 8px;
        th {
            width: 100% !important;
            &:first-of-type {
                border-bottom: none !important;
            }
        }
        td {
            width: 100% !important;
            border-top: none !important;
        }
    }
}
</style>
